import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiService, Request } from "../../api";
import { useParams } from "react-router-dom";
import { Alert, Card, Tag, Tooltip, Button, Space, Spin, message } from "antd";
import { space } from "../../theme/space";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  HighlightOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { ViewApprovers } from "../../components";

import RenderViewComponent from "../../components/RenderViewComponent";
import RenderFormComponent from "../../components/RenderFormComponent";
import WorkflowViewPage from "./WorkflowViewPage";
import ActionsDropdown from "../../components/ActionsDropdown";
import RequestHeader from "../../components/RequestHeader";
import RequestProgress from "../../components/RequestProgress";
import SectionAssigneePicker from "../../components/SectionAssigneePicker";
import SectionSignModal from "../../components/SectionSignModal";
import { CreateStepType } from "../../constants";
import { useRefreshOnTabFocus, useTabTitle } from "../../tabs/tabContext";
import { refreshMyActionsCount } from "../../store/myActionsStore";

const userInfo = localStorage.getItem("userInfo")
  ? JSON.parse(localStorage.getItem("userInfo") || "{}")
  : null;

/**
 * Section types whose editor owns its own Save + Submit pair (see
 * FormViewPage / AssessmentFormComponent). Anything else needs the page to
 * supply a Submit button.
 */
const SELF_SUBMITTING_TYPES = ["Dynamic", "RiskAssessment"];

const ViewRequestPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [data, setData] = useState<Request | undefined>(undefined);
  const [fetch, setFetch] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const workflowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const autoScrolled = useRef(false);
  // The section whose signing modal is open, if any.
  const [signingForm, setSigningForm] = useState<any | null>(null);

  useTabTitle(data?.number);

  const loadRequest = async () => {
    setLoading(true);
    try {
      const response = await ApiService.apiMainRequestsRetrieve(parseInt(id!));
      setData(response);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fetch]);

  // Coming back from the Edit tab (or any other tab that changed this permit)
  // only re-focuses this pane — it never remounts, so without this the page
  // would still show the values it loaded before the edit.
  useRefreshOnTabFocus(loadRequest);

  // Every action on this page — an approval, a section submitted, a status
  // moved — bumps `fetch`, and any of them can add or clear this permit from
  // My Actions. The sidebar is outside the tab workspace, so nothing else
  // would tell it. `fetch > 0` skips the plain first view, which changed
  // nothing.
  useEffect(() => {
    if (fetch > 0) refreshMyActionsCount();
  }, [fetch]);

  const forms: any[] = data?.template?.form_templates ?? [];
  // Stages (if the template uses them) + the request's active stage, so we can
  // show where the request is and which sections are open right now.
  const allStages: any[] = (data as any)?.template?.stages ?? [];
  // A conditional stage this permit's answers rule out is not part of its
  // process: it is shown as skipped, and never offered as a rework target.
  const stagesList = allStages.filter((s) => s.applies !== false);
  const skippedStages = allStages.filter((s) => s.applies === false);
  // The request walks its pinned snapshot; position is a stable stage `key`.
  const currentStageKey: string = (data as any)?.current_stage_key ?? "";
  const skippedFormIds = new Set<number>(
    skippedStages.flatMap((s) => s.form_ids ?? [])
  );
  const skippedWorkflowIds = new Set<number>(
    skippedStages.flatMap((s) => s.workflow_ids ?? [])
  );

  /**
   * Is this section part of this case at all?
   *
   * Two ways for the answer to be no, and the page treats them the same: the
   * step it sits in is ruled out, or the section carries its own rule
   * (`applies_now`, computed by the server against this case's answers). Both
   * mean the same thing to whoever is reading the permit -- there is nothing
   * to fill in here -- so neither blocks progress and both are labelled rather
   * than hidden. Hiding one would take a section that has answers stored in it
   * off the screen entirely.
   */
  const isRuledOut = (ft: any) =>
    skippedFormIds.has(ft.id) || ft.applies_now === false;

  // Only the requester (or a superuser) picks who fills a group-owned section.
  const canAssign =
    !!data &&
    (String((data as any).created_by) === String(userInfo?.id) ||
      !!userInfo?.is_superuser);

  const isOwned = (ft: any) => !!ft.fill_permission && ft.fill_permission !== "Anyone";
  const isSubmitted = (ft: any) => ft.section_status === "Submitted";
  // An optional section is fillable and submittable like any other, it just
  // never blocks the stage — so it stays out of the "you have sections to fill"
  // nag and is labelled as optional instead.
  const isOptional = (ft: any) => ft.is_required === false;
  // editable_now = permission AND the section's stage is the active one. The
  // backend is the source of truth; the UI just reflects it. Every section in
  // the open stage is fillable + submittable here, not only owned ones.
  const canManage = (ft: any) => ft.editable_now === true;
  // Required sections open now and not yet submitted -> banner, scroll, highlight.
  const pending = forms.filter(
    (ft) => canManage(ft) && !isSubmitted(ft) && !isOptional(ft)
  );

  // ---------------------------------------------------------------------
  // "Nothing left to fill in" -> the transition that actually moves the case.
  //
  // The stage machine stops at the last data-entry stage on purpose: crossing
  // into approval is a decision (`advance_data_entry` -> "wait for
  // Send-to-Approval"), not a consequence of the final section being submitted.
  // Nothing on this page said so, so a finished permit looked stuck on the
  // section it had just been signed on.
  // ---------------------------------------------------------------------
  const currentStage = allStages.find((s) => s.key === currentStageKey);
  // Every required section this permit actually walks is submitted.
  const dataEntryDone =
    forms.length > 0 &&
    forms.every(
      (ft) => isRuledOut(ft) || isOptional(ft) || isSubmitted(ft)
    );
  // Invariant 1: found by `kind`, never by name — clients rename statuses.
  const approvalStatus = (data?.status?.next_statuses ?? []).find(
    (s: any) => s.kind === "Approval"
  );
  // Slots with nobody chosen yet, computed by the backend from the very
  // function send-to-approval guards on, so the page cannot disagree with the
  // button. `null` means the serializer did not compute it (a list payload):
  // treat that as unknown and let the server have the last word, never as
  // "nothing is missing".
  const missingSlots: string[] | null =
    (data as any)?.missing_approver_slots ?? null;
  const [advancing, setAdvancing] = useState<boolean>(false);

  // ---------------------------------------------------------------------
  // Choosing who signs, in place.
  //
  // The picker used to exist only as a step of the create/edit wizard, so a
  // requester who created a case and then worked from this page was never
  // offered it — and found out at the end, from a 400 listing six slots with no
  // control anywhere near it. This is the *same* component against the same
  // endpoint; it just stops being wizard-only. No permission moves with it: it
  // appears exactly when the status still offers Edit, i.e. for the people who
  // could already have done this through the wizard.
  // ---------------------------------------------------------------------
  const requestIsEditable = (() => {
    const raw = (data?.status as any)?.system_actions;
    const list: string[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return list.includes("Edit");
  })();
  const currentStageIndex = allStages.findIndex(
    (s) => s.key === currentStageKey
  );
  const stageIndexOfWorkflow = (workflowId: number) =>
    allStages.findIndex((s) => (s.workflow_ids ?? []).includes(workflowId));

  /**
   * A stage's slots stay editable until the stage itself opens. Once it has,
   * its signatures are spawned and `ViewApprovers` is the truth; a *later*
   * stage is still fair game, because `spawn_request_states` reads the picks
   * when that stage opens. A workflow in no stage is legacy — the status's own
   * Edit right is then the only gate, as it always was.
   */
  const canChooseApprovers = (workflow: any) => {
    if (!requestIsEditable || skippedWorkflowIds.has(workflow.id)) return false;
    const index = stageIndexOfWorkflow(workflow.id);
    if (index === -1 || currentStageIndex === -1) return true;
    return index > currentStageIndex;
  };

  const scrollToWorkflow = (workflowId: number) =>
    workflowRefs.current[workflowId]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  const performAction = async (statusId: number) => {
    setAdvancing(true);
    try {
      await ApiService.apiMainPerformActionCreate({
        request_id: parseInt(id!),
        status_id: statusId,
      });
      setFetch((f) => f + 1);
    } catch (e: any) {
      // The backend refuses with the exact list of what is still missing
      // (unassigned approver slots, unsubmitted sections). Show it — a generic
      // failure here leaves the user with nothing to act on.
      const body = e?.body;
      const missing: string[] = Array.isArray(body?.missing) ? body.missing : [];
      message.error(
        [body?.message || body?.detail || t("Something went wrong"), ...missing]
          .filter(Boolean)
          .join(" ")
      );
    } finally {
      setAdvancing(false);
    }
  };

  // The first stage still short of approvers, for the "go and fix it" jump.
  const firstUnassigned = (data?.template?.workflows ?? []).find((w: any) =>
    canChooseApprovers(w)
  );

  const nextStep = (() => {
    if (
      currentStage?.kind !== "DataEntry" ||
      !dataEntryDone ||
      !approvalStatus
    ) {
      return null;
    }
    // Name what is missing while there is somewhere on this page to fix it.
    // Without a picker to send them to, the honest thing is to let the attempt
    // run and show the server's own list.
    if (missingSlots && missingSlots.length > 0 && firstUnassigned) {
      return {
        tone: "blocked" as const,
        message: t(
          "Every section is filled in. {{count}} approval slot(s) still need a name.",
          { count: missingSlots.length }
        ),
        label: t("Choose approvers"),
        onClick: () => scrollToWorkflow(firstUnassigned.id),
      };
    }
    return {
      tone: "ready" as const,
      message: t("Every section is filled in — this case is ready to move on."),
      // The transition's own wording ("Send to Approval"), translated the way
      // every other admin-authored name on this page is.
      label: approvalStatus.action_title
        ? t(approvalStatus.action_title)
        : t("Send for approval"),
      busy: advancing,
      onClick: () => performAction(approvalStatus.id),
    };
  })();

  const scrollToSection = (formId: number) =>
    sectionRefs.current[formId]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  // On first load, jump the owner to the section that needs them.
  useEffect(() => {
    if (!data || autoScrolled.current || pending.length === 0) return;
    autoScrolled.current = true;
    const target = pending[0] as any;
    setTimeout(() => scrollToSection(target.id), 300);
  }, [data, pending]);

  const submitSection = async (
    formId: number,
    submitted: boolean,
    sign?: string
  ) => {
    try {
      await ApiService.apiMainRequestSectionSubmitCreate({
        request_id: parseInt(id!),
        form_id: formId,
        submitted,
        sign,
      });
      message.success(
        submitted ? t("Section marked as done") : t("Section reopened")
      );
      setFetch((f) => f + 1);
    } catch (e: any) {
      // The section-signature guard answers with its own message; show it
      // rather than a generic failure.
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
  };

  // A section may carry its own signature line (Form.signature_kind). When it
  // does, "Submit" opens the signing modal instead of submitting directly.
  const signatureKind = (ft: any) =>
    ft.signature_kind && ft.signature_kind !== "None" ? ft.signature_kind : null;

  const requestSubmit = async (ft: any) => {
    if (signatureKind(ft)) {
      setSigningForm(ft);
      return;
    }
    await submitSection(ft.id, true);
  };

  const toFormProps = (ft: any) => ({
    form_id: ft.id,
    schema: ft.schema_json?.schema,
    form: ft.schema_json?.form,
    formType: ft.type,
    type: CreateStepType.Form,
    system_form_extra_actions: ft.system_form_extra_actions,
  });

  const sectionTag = (ft: any) => {
    // Ruled out by its step's rule or by its own: shown, but plainly not part
    // of this permit's process. The section's own rule can say why, which the
    // step-level one cannot -- the step's rule is about the step.
    if (isRuledOut(ft)) {
      const tag = <Tag>{t("Not required for this request")}</Tag>;
      return ft.applies_now === false && ft.condition_text ? (
        <Tooltip
          title={t("Only asked for when {{rule}}.", { rule: ft.condition_text })}
        >
          {tag}
        </Tooltip>
      ) : (
        tag
      );
    }
    // Tag any section that participates in the fill/submit flow: owned by
    // someone, or open for the current user to fill, or already submitted.
    if (!isOwned(ft) && !canManage(ft) && !isSubmitted(ft)) return null;
    if (isSubmitted(ft)) {
      return (
        <Tag
          color="success"
          icon={ft.section_signed ? <HighlightOutlined /> : <CheckCircleOutlined />}
        >
          {ft.section_signed ? t("Signed") : t("Submitted")}
          {ft.submitted_by_name ? ` · ${ft.submitted_by_name}` : ""}
        </Tag>
      );
    }
    if (canManage(ft)) {
      return (
        <Tag
          color="processing"
          icon={signatureKind(ft) ? <HighlightOutlined /> : <EditOutlined />}
        >
          {signatureKind(ft)
            ? t("You sign this section")
            : t("You fill this section")}
        </Tag>
      );
    }
    return (
      <Tag color="warning" icon={<ClockCircleOutlined />}>
        {t("Waiting")}
      </Tag>
    );
  };

  // First load has nothing to show yet, so the spinner stands alone. Later
  // refreshes keep the permit on screen and dim it instead, so returning from
  // the Edit tab doesn't blank the page and lose your scroll position.
  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: "64px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Spin spinning={loading}>
      <RequestHeader
        request={data}
        actions={
          <ActionsDropdown
            id={id}
            number={data?.number}
            status={data?.status}
            withdraw={(data as any).withdraw}
            fetch={fetch}
            setFetch={setFetch}
          />
        }
      />

      {/* Position in the process and this person's outstanding work, on one
          surface — see RequestProgress for why they were merged. `allStages`,
          not `stagesList`: a stage this permit rules out is still drawn, struck
          through, because "not required here" is information. */}
      <RequestProgress
        stages={allStages}
        currentStageKey={currentStageKey}
        pending={pending}
        onJumpToSection={scrollToSection}
        blocking={(data as any).blocking}
        nextStep={nextStep}
      />

      {data &&
        forms.map((ft: any) => {
          const submitted = isSubmitted(ft);
          const manage = canManage(ft);
          const editable = manage && !submitted;
          return (
            <div
              key={ft.id}
              ref={(el) => (sectionRefs.current[ft.id] = el)}
              // `request-section` is what index.css hangs the nested-card rules
              // on. The cards inside come from 54 stored Formily schemas and
              // cannot be edited from here; the class is how their chrome is
              // quietened without touching a single stored schema.
              className="request-section"
              style={{ marginBottom: space.base }}
            >
              <Card
                title={
                  <Space>
                    {t(ft.title)}
                    {isOptional(ft) && <Tag>{t("Optional")}</Tag>}
                  </Space>
                }
                // 1px, like every other emphasis in the product. A 2px rule
                // here made the one section you can actually type in look like
                // a validation error.
                style={
                  editable
                    ? { borderColor: "#1677ff", boxShadow: "0 0 0 1px #1677ff" }
                    : undefined
                }
                extra={sectionTag(ft)}
              >
                {canAssign && ft.fill_permission === "Group" && (
                  <div style={{ marginBottom: 12 }}>
                    <SectionAssigneePicker
                      requestId={parseInt(id!)}
                      formId={ft.id}
                      groupId={ft.fill_group}
                      groupName={ft.fill_group_name}
                      assigneeId={ft.assignee_id}
                      assigneeName={ft.assignee_name}
                      onChanged={() => setFetch((f) => f + 1)}
                    />
                  </div>
                )}
                {editable ? (
                  <RenderFormComponent
                    form={toFormProps(ft)}
                    requestId={parseInt(id!)}
                    onSuccess={() => {}}
                    // "Submit" (next to Save) saves the values, then marks done
                    // — or opens the signing modal when the section signs.
                    onSubmitSection={() => requestSubmit(ft)}
                    submitLabel={
                      signatureKind(ft) ? t("Sign and submit") : undefined
                    }
                  />
                ) : (
                  <RenderViewComponent
                    form={ft}
                    requestId={parseInt(id!)}
                    readonly={true}
                    onSuccess={() => {}}
                  />
                )}
                {/* A signed section shows what was signed, by whom and when —
                    the on-screen counterpart of the document's signature line. */}
                {submitted && ft.section_signed && (
                  <div style={{ marginTop: 12 }}>
                    {ft.section_signature_image ? (
                      <img
                        src={ft.section_signature_image}
                        alt={t("Signature")}
                        style={{
                          maxHeight: 80,
                          border: "1px solid #f0f0f0",
                          borderRadius: 4,
                          padding: 4,
                          background: "#fff",
                        }}
                      />
                    ) : (
                      <Tag color="success" icon={<HighlightOutlined />}>
                        {t("Signed by EDS")}
                      </Tag>
                    )}
                    <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
                      {ft.submitted_by_name}
                      {ft.section_signed_at
                        ? ` · ${new Date(ft.section_signed_at).toLocaleString()}`
                        : ""}
                    </div>
                  </div>
                )}
                {/* Sections that render Save+Submit themselves put the pair
                    on one row and save before submitting; the remaining types
                    still get a bare Submit here, which relies on their own Save
                    having been clicked first. */}
                {editable && !SELF_SUBMITTING_TYPES.includes(ft.type) && (
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button type="primary" onClick={() => requestSubmit(ft)}>
                      {signatureKind(ft) ? t("Sign and submit") : t("Submit")}
                    </Button>
                  </div>
                )}
                {manage && submitted && (
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button onClick={() => submitSection(ft.id, false)}>
                      {ft.section_signed
                        ? t("Withdraw signature and reopen")
                        : t("Reopen section")}
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          );
        })}

      {data &&
        data.template.workflows.map((w) => {
          const choosing = canChooseApprovers(w);
          return (
            <div
              key={w.id}
              ref={(el) => (workflowRefs.current[w.id] = el)}
              style={{ marginBottom: 16 }}
            >
              <Card
                title={
                  <Space>
                    {t(w.name)}
                    {skippedWorkflowIds.has(w.id) && (
                      <Tag>{t("Not required for this request")}</Tag>
                    )}
                  </Space>
                }
                extra={
                  choosing ? (
                    <Tag color="processing" icon={<TeamOutlined />}>
                      {t("Choose approvers")}
                    </Tag>
                  ) : undefined
                }
              >
                {/* Before a stage opens there is nobody to list, so the card
                    carries the picker instead of an empty approvals table. Once
                    it opens, the table is the truth. */}
                {choosing ? (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={t(
                        "Choose who signs each duty. This case cannot be sent for approval until every slot has a name."
                      )}
                    />
                    <WorkflowViewPage
                      requestId={parseInt(id!)}
                      workflowId={w.id}
                      onSuccess={() => setFetch((f) => f + 1)}
                    />
                  </>
                ) : (
                  <ViewApprovers
                    requestId={id}
                    workflowId={w.id}
                    fetch={fetch}
                    setFetch={setFetch}
                    stages={stagesList}
                    currentStageKey={currentStageKey}
                  />
                )}
              </Card>
            </div>
          );
        })}

      {signingForm && (
        <SectionSignModal
          open={!!signingForm}
          onCancel={() => setSigningForm(null)}
          requestId={id}
          sectionTitle={signingForm.title}
          kind={signatureKind(signingForm) ?? ""}
          onSigned={async (sign) => {
            await submitSection(signingForm.id, true, sign);
            setSigningForm(null);
          }}
        />
      )}
    </Spin>
  );
};

export default ViewRequestPage;
